/* eslint-disable no-unused-expressions */
import { UNAVAILABLE_IMAGE_URL, FULFILLMENT_STATUSES } from '../constants';
import { normalizeStatus, getLineItemKey } from './distribution';
import { IDistributionView, IDistributionViewLineItem } from '../models/active-orders';
import { IOrderDetailsLineItem } from '../models/order-details';

type ILineItem = IDistributionViewLineItem | IOrderDetailsLineItem;

export const isSsr = typeof window === 'undefined';

export const getWindow = () => {
  if (typeof window !== 'undefined') {
    return window;
  }
  return undefined;
};

export const getUrlParam = (key: string) => {
  const searchParams = typeof window !== 'undefined' ? window?.location?.search : '';
  const urlParams = new URLSearchParams(searchParams);
  return urlParams.get(key);
};

export const getTmxId = async (): Promise<string> => {
  let nativeTMXID: string = '';
  if (getWindow()?.__EXPERIENCE_CONTEXT__?.isConsumerApp) {
    nativeTMXID = getUrlParam('guid') ?? '';
  }
  return nativeTMXID || (await getWindow()?.THD_ACCOUNT_LIB?.TMX?.getTMXsessionId()) || '';
};

export const getHost = () => {
  if (isSsr) return '';

  if (process.env.NODE_ENV === 'production' && !process.env.DOMAIN_NAME) {
    return `https://${getWindow()?.location.hostname}`;
  }

  return window.location.origin;
};

const isMobile = /Android|webOS|iPhone|iPad|iPod|Windows Phone|BlackBerry|IEMobile|Opera Mini/i.test(
    getWindow()?.navigator?.userAgent ?? '',
);

export const getChannelID = () => (isMobile ? 2 : 1);

export const generateXB3TraceID = () => {
  const randomPart = Math.random().toString(16).substring(2);
  const timePart = new Date().getTime().toString(16).substring(0, 15);
  return `${randomPart}${timePart}`;
};

export const generateItemImageUrl = (lineItem?: ILineItem): string => {
  if (lineItem?.imageUrl) {
    return lineItem.imageUrl;
  }
  const giftCardImage = (lineItem as IDistributionViewLineItem | undefined)?.giftCard?.thdImageUrl;
  if (giftCardImage) {
    return giftCardImage;
  }
  return UNAVAILABLE_IMAGE_URL;
};

export const getAllFulfillmentGroups = (orders: any[]) => {
  const fulfillmentGroups: any[] = [];
  orders?.forEach((order) => {
    // Add status checks here if needed in the future
    const {
      POJobName,
      projectName,
      orderNumber,
      orderOrigin,
      salesDate,
      transactionId,
      registerNumber,
      storeNumber,
      orderNumbers,
    } = order || {};
    order?.fulfillmentGroups?.forEach((fg: any) => {
      // Add status checks here if needed in the future
      fulfillmentGroups.push({
        ...fg,
        poJobName: POJobName,
        projectName,
        orderNumber,
        orderOrigin,
        salesDate,
        transactionId,
        registerNumber,
        storeNumber,
        orderNumbers,
      });
    });
  });
  return fulfillmentGroups;
};

/**
 * The items to show for one distribution, with duplicates merged.
 *
 * Status lives on the distribution, not the item, so a canceled or returned
 * distribution drops all of its items at once. Items are told apart by
 * `lineId`; without a key every item would compare equal and the whole card
 * would collapse into one row with a summed quantity.
 */
export const filterAndConsolidateLineItems = (distribution?: IDistributionView) => {
  const status = normalizeStatus(distribution?.status, distribution?.distributionType);

  if (status === FULFILLMENT_STATUSES.CANCELED || status === FULFILLMENT_STATUSES.RETURNED) {
    return [];
  }

  const allLineItems: IDistributionViewLineItem[] = [];

  distribution?.lineItems?.forEach((item: IDistributionViewLineItem, index: number) => {
    const key = getLineItemKey(item, index, distribution?.orderNumber);
    const existingItem = allLineItems.find((existing) => existing.lineItemKey === key);
    const itemQuantity = item?.quantity ?? 0;

    if (existingItem) {
      existingItem.quantity = (existingItem.quantity ?? 0) + itemQuantity;
    } else {
      allLineItems.push({ ...item, quantity: itemQuantity, lineItemKey: key });
    }
  });

  return allLineItems;
};

export const formatPhoneNumber = (phoneNumber: string) => {
  if (phoneNumber.length === 10) {
    return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6)}`;
  }

  if (phoneNumber.length === 11) {
    return `${phoneNumber.slice(0, 1)}-${phoneNumber.slice(
      1,
      4,
    )}-${phoneNumber.slice(4, 7)}-${phoneNumber.slice(7)}`;
  }

  return phoneNumber;
};

export const formatPrice = (
  value: number,
  options: { minimumFractionDigits?: number; maximumFractionDigits?: number; locale?: string } = {},
) => {
  if (value == null) return '';

  const { minimumFractionDigits = 2, maximumFractionDigits = 2, locale = 'en-US' } = options;

  return value.toLocaleString(locale, {
    minimumFractionDigits,
    maximumFractionDigits,
  });
};

export const buildQueryString = ({
  index,
  orderNumber,
  orderNumbers,
  orderOrigin,
  registerNumber,
  salesDate,
  storeNumber,
  transactionId,
  transactionType,
  nickName,
  deliveryDate,
  workorderExceptions,
  POJobName,
  projectName,
  status,
  localDelivery,
}: {
  index?: number;
  orderNumber?: string;
  orderNumbers?: string[];
  orderOrigin?: string;
  registerNumber?: string;
  salesDate?: string;
  storeNumber?: string;
  transactionId?: string;
  transactionType?: string;
  nickName?: string;
  deliveryDate?: string;
  workorderExceptions?: string;
  POJobName?: string;
  projectName?: string;
  status?: string;
  localDelivery?: boolean;
}) => {
  const formattedOrderOrigin = orderOrigin?.replace('#', '%23');

  const transactionTypeModifier = (type: string) => {
    switch (type) {
    case 'Return':
      return 'R';
    case 'Sale':
      return 'S';
    default:
      return type;
    }
  };

  const orderNo = orderNumbers?.[0] ?? orderNumber;

  const paramMap = {
    orderNumber: orderNo,
    salesDate,
    storeNumber,
    transactionId,
    registerNumber,
    transactionType: transactionType ? transactionTypeModifier(transactionType) : undefined,
    deliveryDate,
    POJobName: POJobName?.trim(),
    projectName,
    status,
    workorderExceptions,
    orderOrigin: formattedOrderOrigin?.trim(),
    nickName,
    index: index != null ? String(index) : undefined,
    localDelivery,
  };

  const params = new URLSearchParams();

  Object.entries(paramMap).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      params.append(key, String(value));
    }
  });

  return `?${params.toString()}`;
};

/**
 * The quantity the line renders and is priced against.
 *
 * `quantity` is what was ordered; `currentQuantity` is what is left after a
 * partial cancellation, and is only read when the ordered quantity is missing.
 * The other spellings are the legacy order-details payload's, kept because a
 * consolidated card item can still carry them.
 *
 * Undefined when the item names no quantity at all. Callers must guard on it:
 * rendering it straight gives a bare "Qty: ", and defaulting it to 1 prices
 * the line as if one were ordered.
 */
export const getItemQuantity = (item?: ILineItem) => item?.quantity
  ?? item?.currentQuantity
  ?? (item as IDistributionViewLineItem)?.originalOrderedQuantity
  ?? (item as IDistributionViewLineItem)?.orginalOrderedQuantity
  ?? (item as IDistributionViewLineItem)?.qtyToPrint;
